import { useEffect, useRef, useState } from "react";
import { Loader2, Check, Plus, FolderPlus, X } from "lucide-react";
import {
  getCollections,
  createCollection,
  addPhotoToCollection,
  removePhotoFromCollection,
  getCollectionsForPhoto,
} from "../lib/api";

export default function SaveToCollectionPopover({ photo, open, onClose, anchorRef }) {
  const [collections, setCollections] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [busyId, setBusyId] = useState(null);
  const panelRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    Promise.all([getCollections(), getCollectionsForPhoto(photo.id)])
      .then(([colls, ids]) => {
        setCollections(colls);
        setSelected(new Set(ids));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [open, photo.id]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(e.target) &&
        anchorRef?.current &&
        !anchorRef.current.contains(e.target)
      ) {
        onClose();
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open, onClose, anchorRef]);

  if (!open) return null;

  const toggle = async (coll) => {
    setBusyId(coll.id);
    const isIn = selected.has(coll.id);
    // optimistic
    setSelected((prev) => {
      const next = new Set(prev);
      if (isIn) next.delete(coll.id);
      else next.add(coll.id);
      return next;
    });
    try {
      if (isIn) await removePhotoFromCollection(coll.id, photo.id);
      else await addPhotoToCollection(coll.id, photo);
      // refresh cover/count for the affected collection in local state
      const fresh = await getCollections();
      setCollections(fresh);
    } catch {
      // rollback
      setSelected((prev) => {
        const next = new Set(prev);
        if (isIn) next.add(coll.id);
        else next.delete(coll.id);
        return next;
      });
    } finally {
      setBusyId(null);
    }
  };

  const create = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const c = await createCollection(newName.trim());
      await addPhotoToCollection(c.id, photo);
      const fresh = await getCollections();
      setCollections(fresh);
      setSelected((prev) => new Set(prev).add(c.id));
      setNewName("");
    } catch { /* ignore create errors, keep panel open */ } finally {
      setCreating(false);
    }
  };

  return (
    <div
      ref={panelRef}
      data-testid="save-collection-popover"
      className="absolute right-0 top-full mt-2 w-80 bg-white border border-border shadow-2xl z-50"
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <div className="font-serif text-base text-ink">Save to collection</div>
        <button
          onClick={onClose}
          className="h-7 w-7 inline-flex items-center justify-center hover:bg-secondary"
          aria-label="Close"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className="max-h-64 overflow-y-auto">
        {loading && (
          <div className="p-6 text-center text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading…
          </div>
        )}
        {!loading && collections.length === 0 && (
          <div className="p-6 text-center text-sm text-muted-foreground">
            No collections yet. Create your first below.
          </div>
        )}
        {!loading && collections.map((c) => {
          const isIn = selected.has(c.id);
          return (
            <button
              key={c.id}
              onClick={() => toggle(c)}
              data-testid={`save-collection-item-${c.id}`}
              disabled={busyId === c.id}
              className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-secondary text-left disabled:opacity-60"
            >
              <span className="h-10 w-10 shrink-0 bg-secondary overflow-hidden" style={{ backgroundColor: c.cover?.avg_color || "#e9e5dd" }}>
                {c.cover?.src?.small && (
                  <img src={c.cover.src.small} alt="" className="h-full w-full object-cover" />
                )}
              </span>
              <span className="flex-1 min-w-0">
                <span className="block text-sm text-ink truncate">{c.name}</span>
                <span className="block text-[11px] text-muted-foreground">{c.photo_count} photos</span>
              </span>
              <span
                className={`h-5 w-5 rounded-full border flex items-center justify-center ${
                  isIn ? "bg-gold border-gold text-ivory" : "border-border"
                }`}
              >
                {busyId === c.id ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : isIn ? (
                  <Check className="h-3 w-3" />
                ) : null}
              </span>
            </button>
          );
        })}
      </div>

      <form onSubmit={create} className="p-3 border-t border-border flex gap-2">
        <div className="flex-1 flex items-center border border-border">
          <FolderPlus className="h-4 w-4 ml-2 text-muted-foreground" />
          <input
            data-testid="save-collection-new-input"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="New collection name"
            className="flex-1 px-2 py-1.5 text-sm outline-none"
            maxLength={80}
          />
        </div>
        <button
          type="submit"
          data-testid="save-collection-create-btn"
          disabled={creating || !newName.trim()}
          className="px-3 py-1.5 bg-gold text-ivory text-sm disabled:opacity-50 inline-flex items-center gap-1"
        >
          {creating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
          Create
        </button>
      </form>
    </div>
  );
}
